import type { ClassificationDialogLabel, ClassificationDialogPolicy } from '@core/domain';
import { Badge, BadgeSize, Eye, IconButton, IconButtonSize, IconSize, StatusCircleBlock } from '@soteria/design-system-ve';

import * as styles from './ClassificationDialog.css';

interface ContentSectionProps {
  text?: string;
  applyBlur: boolean;
  isContentBlurred: boolean;
  isSafe: boolean;
  onToggleBlur: () => void;
}

/** V1 Content/Text Section - returns null if no text */
export function V1ContentSection({ text, applyBlur, isContentBlurred, isSafe, onToggleBlur }: ContentSectionProps) {
  if (!text) return null;

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Content</h3>
      <div className={styles.contentBoxContainer}>
        <div className={`${styles.contentBox} ${applyBlur ? styles.blurredText : ''}`}>{text}</div>
        {!isSafe && (
          <IconButton
            buttonSize={IconButtonSize.Small}
            className={styles.hoverButton}
            icon={isContentBlurred ? Eye : StatusCircleBlock}
            iconSize={IconSize.Small}
            title={isContentBlurred ? 'Show content' : 'Hide content'}
            onClick={onToggleBlur}
          />
        )}
      </div>
    </div>
  );
}

/** V1 Explanation Section - returns null if no explanation */
export function V1ExplanationSection({ explanation }: { explanation?: string }) {
  if (!explanation) return null;

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Explanation</h3>
      <div className={styles.contentBox}>{explanation}</div>
    </div>
  );
}

interface MetadataSectionProps {
  confidenceScore?: number;
  language?: string;
  duration?: number;
}

/** V1 Metadata Section - returns null if no metadata */
export function V1MetadataSection({ confidenceScore, language, duration }: MetadataSectionProps) {
  const hasMetadata = confidenceScore !== undefined || language !== undefined || duration !== undefined;

  if (!hasMetadata) return null;

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Classification Details</h3>
      <div className={styles.metadataGrid}>
        {confidenceScore !== undefined && (
          <div className={styles.metadataItemInline}>
            <span className={styles.metadataLabel}>Confidence:</span>
            <span className={styles.metadataValue}>{(confidenceScore * 100).toFixed(1)}%</span>
          </div>
        )}
        {language && (
          <div className={styles.metadataItemInline}>
            <span className={styles.metadataLabel}>Language:</span>
            <span className={styles.metadataValue}>{language}</span>
          </div>
        )}
        {duration !== undefined && (
          <div className={styles.metadataItemInline}>
            <span className={styles.metadataLabel}>Processing Time:</span>
            <span className={styles.metadataValue}>{duration}ms</span>
          </div>
        )}
      </div>
    </div>
  );
}

/** V1 Label Item */
function V1LabelItem({ label, isSafe }: { label: ClassificationDialogLabel; isSafe: boolean }) {
  const styleClass = isSafe ? styles.categoryItemSafe : styles.categoryItemUnsafe;

  return (
    <div className={`${styles.categoryItem} ${styleClass}`}>
      <div className={styles.categoryInfo}>
        <span className={styles.categoryName}>{label.name}</span>
        {label.shortName && label.shortName !== label.name && <span className={styles.categoryShortName}>({label.shortName})</span>}
      </div>
      <div className={styles.categoryDetails}>
        <div className={styles.categoryMetric}>
          <span className={styles.categoryLabel}>Severity:</span>
          <span className={styles.severityValue}>{label.severity}</span>
        </div>
      </div>
    </div>
  );
}

/** V1 Labels Section - returns null if no labels */
export function V1LabelsSection({ labels, isSafe }: { labels: ClassificationDialogLabel[]; isSafe: boolean }) {
  if (labels.length === 0) return null;

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Labels</h3>
      <div className={styles.categoriesList}>
        {labels.map(label => (
          <V1LabelItem key={`${label.name}-${label.severity}`} isSafe={isSafe} label={label} />
        ))}
      </div>
    </div>
  );
}

/** V1 Policy Subsection */
function V1PolicySubsection({ title, policies }: { title: string; policies: ClassificationDialogPolicy[] }) {
  if (policies.length === 0) return null;

  return (
    <div className={styles.policySubsection}>
      <span className={styles.policySubsectionTitle}>
        {title} ({policies.length})
      </span>
      <div className={styles.policyList}>
        {policies.map(policy => (
          <Badge key={policy.id} size={BadgeSize.Small} text={policy.name} title={policy.description} />
        ))}
      </div>
    </div>
  );
}

interface PoliciesSectionProps {
  triggeredPolicies?: ClassificationDialogPolicy[];
  nonTriggeredPolicies?: ClassificationDialogPolicy[];
}

/** V1 Policies Section - returns null if no policies */
export function V1PoliciesSection({ triggeredPolicies, nonTriggeredPolicies }: PoliciesSectionProps) {
  const hasPolicies = (triggeredPolicies?.length ?? 0) > 0 || (nonTriggeredPolicies?.length ?? 0) > 0;

  if (!hasPolicies) return null;

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Policies</h3>
      <div className={styles.policySectionsRow}>
        <V1PolicySubsection policies={triggeredPolicies ?? []} title="Triggered" />
        <V1PolicySubsection policies={nonTriggeredPolicies ?? []} title="Not Triggered" />
      </div>
    </div>
  );
}
